/**
 * Typed wrappers around the Sprint 3 module-quiz + reflection endpoints.
 *
 * Backend reference: ``backend/caitapi/routers/modules.py``. Types mirror
 * the Pydantic response models — keep in sync when those change.
 *
 * Decisions encoded here:
 *
 * - **DEC-0011** — pool draws across the module's lesson KC pools; the
 *   client doesn't need to know this, but ``items.length === 10`` per
 *   spec §5.1 (vs. the lesson KC's 5).
 * - **DEC-0012** — cooldown surfaces as a 422 with ``detail.blocking ===
 *   "cooldown_active"`` and ``detail.cooldown_until``. The wrappers
 *   re-throw the parsed detail so the UI can render a countdown banner.
 * - **DEC-0014** — on pass the submit response carries
 *   ``should_prompt_reflection: true``. The UI then renders the
 *   reflection prompt inline before navigating to the next module.
 */
import { apiGet, apiPost } from "./api";
import type {
  KCClientItem,
  SubmitReviewItem,
} from "./lesson-api";

// ---------------------------------------------------------------------------
// Start
// ---------------------------------------------------------------------------

export interface StartModuleQuizResponse {
  attempt_id: string;
  module_id: string;
  attempt_number: number;
  items: KCClientItem[];
  passing_percent: number;
}

export interface CooldownDetail {
  blocking: "cooldown_active";
  cooldown_until: string; // ISO 8601
  consecutive_failed_attempts: number;
  failure_cap: number;
  message: string;
}

export interface ModuleLockedDetail {
  blocking: "module_quiz_not_passed";
  blocking_module_id: string;
  blocking_module_sequence: number;
  message: string;
}

/**
 * Custom Error subclass thrown by ``startModuleQuiz`` when the server
 * returns 422 with a cooldown detail. The component renders the
 * countdown by reading ``.cooldown_until``.
 */
export class ModuleQuizCooldownError extends Error {
  readonly cooldown_until: Date;
  readonly consecutive_failed_attempts: number;
  readonly failure_cap: number;

  constructor(detail: CooldownDetail) {
    super(detail.message);
    this.name = "ModuleQuizCooldownError";
    this.cooldown_until = new Date(detail.cooldown_until);
    this.consecutive_failed_attempts = detail.consecutive_failed_attempts;
    this.failure_cap = detail.failure_cap;
  }
}

/**
 * Custom Error subclass thrown when the server returns 403 with
 * ``blocking: module_quiz_not_passed``. Surfaced so the UI can route
 * the user back to the gating module instead of showing a generic 403.
 */
export class ModuleLockedError extends Error {
  readonly blocking_module_id: string;
  readonly blocking_module_sequence: number;

  constructor(detail: ModuleLockedDetail) {
    super(detail.message);
    this.name = "ModuleLockedError";
    this.blocking_module_id = detail.blocking_module_id;
    this.blocking_module_sequence = detail.blocking_module_sequence;
  }
}

export async function startModuleQuiz(
  moduleId: string,
): Promise<StartModuleQuizResponse> {
  try {
    return await apiPost<StartModuleQuizResponse>(
      `/api/modules/${encodeURIComponent(moduleId)}/quiz/start`,
    );
  } catch (err) {
    // apiPost throws ApiError whose ``body`` is the parsed JSON response.
    // FastAPI wraps HTTPException.detail as ``{ detail: <our payload> }``.
    // Inspect that to surface the structured cooldown / locked errors.
    const body = (err as { body?: unknown }).body;
    const detail =
      body !== undefined && body !== null && typeof body === "object"
        ? (body as { detail?: unknown }).detail
        : undefined;
    if (detail !== undefined && detail !== null && typeof detail === "object") {
      const d = detail as { blocking?: string };
      if (d.blocking === "cooldown_active") {
        throw new ModuleQuizCooldownError(detail as unknown as CooldownDetail);
      }
      if (d.blocking === "module_quiz_not_passed") {
        throw new ModuleLockedError(detail as unknown as ModuleLockedDetail);
      }
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Answer
// ---------------------------------------------------------------------------

export interface RecordModuleQuizAnswerResponse {
  attempt_id: string;
  answered_count: number;
  total_items: number;
}

export function recordModuleQuizAnswer(
  attemptId: string,
  itemId: string,
  optionId: string,
): Promise<RecordModuleQuizAnswerResponse> {
  return apiPost<RecordModuleQuizAnswerResponse>(
    `/api/module-quiz/${encodeURIComponent(attemptId)}/answer`,
    { item_id: itemId, option_id: optionId },
  );
}

// ---------------------------------------------------------------------------
// Submit
// ---------------------------------------------------------------------------

export interface SubmitModuleQuizResponse {
  attempt_id: string;
  module_id: string;
  attempt_number: number;
  score: number;
  passed: boolean;
  passing_percent: number;
  items: SubmitReviewItem[];
  should_prompt_reflection: boolean;
  next_module_unlocked: boolean;
  /** Set iff this submit triggered the 3rd consecutive failure. ISO timestamp. */
  cooldown_until: string | null;
}

export function submitModuleQuiz(
  attemptId: string,
): Promise<SubmitModuleQuizResponse> {
  return apiPost<SubmitModuleQuizResponse>(
    `/api/module-quiz/${encodeURIComponent(attemptId)}/submit`,
  );
}

// ---------------------------------------------------------------------------
// Reflection journals (FR-L-1..4)
// ---------------------------------------------------------------------------

export interface ReflectionEntry {
  id: string;
  module_id: string;
  body: string | null;
  created_at: string;
  updated_at: string;
}

export interface MyJournalEntry {
  id: string;
  module_id: string;
  module_sequence: number;
  module_title: string;
  module_slug: string;
  body: string | null;
  created_at: string;
  updated_at: string;
}

export function writeReflection(
  moduleId: string,
  body: string | null,
): Promise<ReflectionEntry> {
  return apiPost<ReflectionEntry>(
    `/api/modules/${encodeURIComponent(moduleId)}/reflection`,
    { body },
  );
}

export function listModuleReflections(
  moduleId: string,
): Promise<ReflectionEntry[]> {
  return apiGet<ReflectionEntry[]>(
    `/api/modules/${encodeURIComponent(moduleId)}/reflection`,
  );
}

export function getMyJournal(): Promise<MyJournalEntry[]> {
  return apiGet<MyJournalEntry[]>("/api/reflections");
}
